import { isNil } from 'lodash';
import React from 'react';
import { MBLoadingIndicator } from '@shared/components/loading';
import {
  NO_CODE_CREATE_FORM,
  NO_CODE_UPDATE_FORM,
  useFeatureFlag,
} from '@shared/util/featureFlags/launchDarkly';
import { useChannel } from '@shared/util/hooks';
import { CardDetails } from './CardDetails';
import { CreateCardEntityForm } from './CreateCardEntityForm';
import { ExternalCardForm } from './ExternalCardForm';
import { ReadOnlyCardEntityForm } from './ReadOnlyCardEntityForm';

interface ExternalContentProps {
  cardId?: string | null;
  channelId: number;
  templateSlug?: string;
}

export function ExternalContent({ cardId, channelId, templateSlug }: ExternalContentProps) {
  const channel = useChannel({ channelId });
  if (isNil(channel)) {
    return <MBLoadingIndicator fill center />;
  }

  if (!isNil(cardId)) {
    return <ExternalCard channelId={channelId} cardId={cardId} templateSlug={templateSlug} />;
  } else {
    return <ExternalForm channelId={channelId} templateSlug={templateSlug} />;
  }
}

interface ExternalCardProps extends Omit<ExternalContentProps, 'cardId'> {
  cardId: string;
}

function ExternalCard({ cardId, channelId, templateSlug }: ExternalCardProps) {
  const isNoCodeUpdateFormEnabled = useFeatureFlag(NO_CODE_UPDATE_FORM);
  const channel = useChannel({ channelId });
  const isNoCodeWorkspace = !isNil(channel?.workflowTemplateId);

  if (isNoCodeWorkspace && isNoCodeUpdateFormEnabled && !isNil(templateSlug)) {
    return (
      <ReadOnlyCardEntityForm channelId={channelId} templateSlug={templateSlug} entityId={cardId} />
    );
  } else {
    return <CardDetails cardId={cardId} channelId={channelId} templateSlug={templateSlug} />;
  }
}

type ExternalFormProps = Omit<ExternalContentProps, 'cardId'>;

function ExternalForm({ channelId, templateSlug }: ExternalFormProps) {
  const isNoCodeCreateFormEnabled = useFeatureFlag(NO_CODE_CREATE_FORM);
  const channel = useChannel({ channelId });
  const isNoCodeWorkspace = !isNil(channel?.workflowTemplateId);

  if (isNoCodeWorkspace && isNoCodeCreateFormEnabled && !isNil(templateSlug)) {
    return <CreateCardEntityForm channelId={channelId} templateSlug={templateSlug} />;
  } else {
    return <ExternalCardForm channelId={channelId} templateSlug={templateSlug} />;
  }
}
